import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/client';
import { describeDelivery } from '@/components/pickupFormat';
import { PickupHeadOfficePicker } from '@/components/PickupHeadOfficePicker';
import { hasPickupDrifted } from '@/types';
import type { PickupDeliveryRequest, PickupProducer, SubscriptionPickupState } from '@/types';

/**
 * The pickup destination on the CREATE form — a checkbox and a client name, held as form state.
 *
 * Its own component rather than a mode of {@link PickupToggle} because on create there is nothing
 * to toggle yet: no subscription exists, so there is nothing to attach a pickup route to and
 * nothing to undo. It is a choice the form carries and the create call acts on, which is why it
 * sits with the recipients — a destination alongside the people, not a separate act.
 */
export function PickupDestinationChoice({
  value,
  onChange,
  suggestedClientName
}: {
  value: PickupDeliveryRequest | null;
  onChange: (value: PickupDeliveryRequest | null) => void;
  /** Best guess from the report's parameters, so the common case needs no typing. */
  suggestedClientName?: string | null;
}) {
  const api = useApi();
  const { data: status } = useQuery({ queryKey: ['pickup-status'], queryFn: api.pickupStatus });

  const enabled = value?.enabled === true;

  // Not configured is stated rather than hidden. A missing option reads as a bug to anyone who has
  // seen it elsewhere, and "not in this environment yet" is a real and temporary answer.
  if (status && !status.isConfigured) {
    return (
      <div className="field-hint">
        Report pickup is not configured in this environment yet, so this subscription can only
        deliver by email.
      </div>
    );
  }

  return (
    <div>
      <label className="checkbox-field">
        <input
          type="checkbox"
          checked={enabled}
          onChange={e =>
            onChange(e.target.checked
              ? { enabled: true, clientName: suggestedClientName ?? null, clientKey: suggestedClientName ?? null }
              : null)}
        />
        Also file every run into report pickup
      </label>
      <div className="field-hint">
        The commercial team can search for and download it there, and every download is recorded.
        The email above is unaffected.
      </div>

      {enabled && (
        <>
          <div className="field" style={{ marginTop: 10, maxWidth: 380 }}>
            <label>Client / head office</label>
            <PickupHeadOfficePicker
              name={value?.clientName ?? ''}
              headOfficeId={value?.headOfficeId ?? null}
              onChange={({ name, headOfficeId }) => onChange({
                enabled: true,
                clientName: name || null,
                clientKey: name || null,
                headOfficeId
              })}
            />
          </div>

          {/* Said out loud rather than hidden, because it is visible on MANDW01 and somebody will
              eventually go looking. The portal presents the pair as one thing everywhere else. */}
          <div className="field-hint" style={{ marginTop: 6 }}>
            A report server subscription carries one delivery method, so this creates a second
            subscription there sharing this report, its parameters and its schedule. The portal
            manages the two together — editing, disabling or deleting this one does the same to both.
          </div>
        </>
      )}
    </div>
  );
}

/**
 * The warning that a pair has been broken from outside the portal.
 *
 * Renders nothing for a healthy or an email-only subscription, so it can sit unconditionally at the
 * top of the detail page. `Unknown` gets a quieter notice rather than none: the portal not being
 * able to check is worth saying, but it is not evidence of breakage.
 */
export function PickupDriftNotice({ pickup }: { pickup?: SubscriptionPickupState | null }) {
  if (!pickup || pickup.health === 'None' || pickup.health === 'Paired') return null;

  const unknown = pickup.health === 'Unknown';
  const { detail } = describeDelivery(pickup);

  return (
    <div className={unknown ? 'notice' : 'notice notice-warn'} style={{ marginBottom: 18 }}>
      <strong>
        {unknown
          ? 'The report pickup delivery could not be checked.'
          : 'This subscription’s report pickup delivery is broken.'}
      </strong>
      <div>
        {detail}{' '}
        {!unknown && (
          <>
            Rebuild it from the Recipients and delivery section below, or repair the file-share
            subscription in Report Server Schedule Manager.
          </>
        )}
      </div>
    </div>
  );
}

/**
 * The "also deliver to report pickup" option, for both subscription types.
 *
 * It is a separate control rather than a field inside the subscription form because for a report
 * server subscription it is genuinely a separate thing: a PBIRS subscription carries exactly one
 * delivery extension, so pickup alongside email is a second subscription on MANDW01 sharing the
 * report, parameters and schedule. Saving the form does not create or remove it; this does, and
 * says so.
 *
 * There is one rendering, not two. It used to have a `compact` mode for the KT Insights list's
 * Pickup column — state and a single button, no client field and no storage path — and that column
 * now reports which delivery methods an export has rather than offering to change them, so the only
 * caller of the compact branch is gone. It went with it: a mode kept "in case" is a second layout
 * of an estate-changing control that nothing exercises and nobody would notice going wrong.
 */
export function PickupToggle({
  subscriptionId,
  producer,
  suggestedClientName,
  pickup
}: {
  subscriptionId: string;
  producer: PickupProducer;
  suggestedClientName?: string | null;
  /**
   * The verified state of the pair, when the caller has it.
   *
   * Without it this control can only see the portal's own delivery row, which says "on" right up
   * until somebody notices no files have arrived for three months — the row stays enabled however
   * broken the report server's half is. Passing it in is what stops the panel reassuring somebody
   * that a delivery is working while the banner above says it is not.
   */
  pickup?: SubscriptionPickupState | null;
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [clientName, setClientName] = useState(suggestedClientName ?? '');
  const [headOfficeId, setHeadOfficeId] = useState<string | null>(null);

  const { data: status } = useQuery({ queryKey: ['pickup-status'], queryFn: api.pickupStatus });
  const { data: deliveries } = useQuery({ queryKey: ['pickup-deliveries'], queryFn: api.pickupDeliveries });

  const delivery = deliveries?.find(d => d.sourceSubscriptionId === subscriptionId && d.isEnabled);
  const drifted = hasPickupDrifted(pickup);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['pickup-deliveries'] });
    void queryClient.invalidateQueries({ queryKey: ['pickup-files'] });
    // The subscription itself and the list both state where it delivers, and both are now wrong.
    // Without these the page keeps saying "Email" beside a control that has just switched pickup on.
    void queryClient.invalidateQueries({ queryKey: ['sub', subscriptionId] });
    void queryClient.invalidateQueries({ queryKey: ['mine'] });
  };

  const enable = useMutation({
    mutationFn: () => api.pickupEnable({
      subscriptionId,
      producer,
      clientName: clientName.trim() || null,
      clientKey: clientName.trim() || null,
      headOfficeId
    }),
    onSuccess: refresh
  });

  const disable = useMutation({
    mutationFn: () => api.pickupDisable(subscriptionId),
    onSuccess: refresh
  });

  if (status && !status.isConfigured) {
    return <div className="field-hint">Report pickup is not configured in this environment yet.</div>;
  }

  if (delivery) {
    return (
      <div>
        {drifted ? (
          // The delivery row still reads "on" — it always will, because it is the portal's record
          // of intent and nothing on MANDW01 updates it. Saying "delivering to report pickup" on
          // top of a route that has been broken for weeks is the one thing this panel must not do.
          <div className="notice notice-warn">
            <strong>This pickup delivery has stopped working.</strong>
            <div>
              {describeDelivery(pickup).detail}{' '}
              Rebuild it below: the files already collected stay where they are, and the rebuilt
              delivery writes into the same folder beside them.
            </div>
          </div>
        ) : (
          // Overdue takes the warning tone rather than a closing sentence inside a neutral box.
          // The pair is intact here — nothing has drifted — so the only thing saying a scheduled
          // run produced no file is this line, and "Delivering to report pickup" in the ordinary
          // style above it reads as reassurance that contradicts it.
          <div className={delivery.isOverdue ? 'notice notice-warn' : 'notice'}>
            <strong>
              {delivery.isOverdue
                ? 'A scheduled run has not produced a file.'
                : 'Delivering to report pickup.'}
            </strong>{' '}
            {delivery.isOverdue && delivery.expectedByUtc
              ? `One was expected by ${new Date(delivery.expectedByUtc).toLocaleString()} and none has arrived. `
              : ''}
            {delivery.fileCount === 0
              ? 'No files collected yet'
              : `${delivery.fileCount} file${delivery.fileCount === 1 ? '' : 's'} collected`}
            {delivery.lastFileUtc && `, most recent ${new Date(delivery.lastFileUtc).toLocaleDateString()}`}.
            {delivery.clientFolder && ` Filed under ${delivery.clientFolder}.`}
            {/* Deliberately not "rebuild it": the report server's half is present and correct, so
                there is nothing here to repair. The run itself is what to go and look at. */}
            {delivery.isOverdue && ' The pickup route is intact, so check the run rather than the delivery.'}
          </div>
        )}
        {/* The UNC rather than the share-relative path: this is the string the BI team need if
            they ever have to check what the report server was actually told to write. It carries a
            year, and the year changes — the portal repoints the route each January and the folder
            it moves to has existed for a year by then. Nothing here mentions retention, because
            nothing in pickup is ever deleted or moved; prior years stay in their own folder. */}
        <div className="field-hint" style={{ marginTop: 6 }}>
          Kept indefinitely. Written to <code>{delivery.uncPath}</code>.
        </div>
        {delivery.renderedAsEmail && (
          <div className="field-hint" style={{ marginTop: 4 }}>
            Rendered as <strong>{delivery.renderedAsName ?? delivery.renderedAsEmail}</strong>
            {delivery.renderedAsRoleName ? ` (${delivery.renderedAsRoleName})` : ''} — the export
            shows that person’s view of the data, so what lands in the file depends on it.
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          {/* Rebuild is the whole repair. Enabling an already-enabled route re-creates the missing
              file-share subscription and reuses the folder this delivery already owns, so new files
              land beside the ones already collected rather than starting a second folder. */}
          {drifted && (
            <button
              type="button"
              className="btn-primary btn-sm"
              disabled={enable.isPending}
              onClick={() => enable.mutate()}
            >
              {enable.isPending ? 'Rebuilding…' : 'Rebuild pickup delivery'}
            </button>
          )}
          <button
            type="button"
            className="btn-secondary btn-sm"
            disabled={disable.isPending}
            onClick={() => {
              if (!confirm(
                'Stop delivering this report to pickup?\n\n'
                + 'The second subscription on the report server is deleted, so no new files are written. '
                + 'Everything already collected stays available and downloadable, and switching pickup '
                + 'back on later resumes into the same folder.')) return;
              disable.mutate();
            }}
          >
            {disable.isPending ? 'Turning off…' : 'Turn off pickup'}
          </button>
        </div>
        {(disable.error || enable.error) && (
          <div className="error-banner">
            {((disable.error ?? enable.error) as Error).message.replace(/^API \d+: /, '')}
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="field-hint">
        Also write each run's output to report pickup, where the commercial team can search for
        and download it. Email delivery is unaffected.
      </div>
      <div className="field-row" style={{ marginTop: 8 }}>
        <div className="field" style={{ margin: 0 }}>
          <label>Client / head office</label>
          <PickupHeadOfficePicker
            name={clientName}
            headOfficeId={headOfficeId}
            onChange={({ name, headOfficeId: id }) => { setClientName(name); setHeadOfficeId(id); }}
          />
        </div>
      </div>
      <button
        type="button"
        className="btn-secondary btn-sm"
        style={{ marginTop: 8 }}
        disabled={enable.isPending}
        onClick={() => enable.mutate()}
      >
        {enable.isPending ? 'Setting up…' : 'Deliver to report pickup'}
      </button>
      {enable.error && (
        <div className="error-banner">{(enable.error as Error).message.replace(/^API \d+: /, '')}</div>
      )}
    </div>
  );
}
